import { FormFieldLabel, radius } from '@alison/ui';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlined from '@mui/icons-material/KeyboardArrowUpOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import {
  type AnalysedAttribute,
  type AnalysedFeature,
  analysedAttributes,
  FEATURE_SCORE_MAX,
} from './attributesAnalysis.js';
import { ScorePip, toneOf } from './ScoreVisuals.js';

const SCORE_SCALE = 10;

const FRAMEWORK_TEXT: Record<string, string> = {
  google: "Performance indicators based on Google's ABCD creative framework",
  meta: "Performance indicators based on Meta's creative framework",
  linkedin: "Performance indicators based on LinkedIn's creative framework",
  tiktok: "Performance indicators based on TikTok's creative framework",
};

const CARD_SX = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const HEADER_SX = {
  gap: 2,
  px: 4,
  py: 2.5,
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
};

const ROW_SX = {
  gap: 2,
  px: 2,
  py: 1.5,
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: 2,
  bgcolor: 'background.page',
};

const SOURCE_SX = {
  gap: 0.5,
  px: 1.5,
  py: 0.5,
  alignItems: 'center',
  borderRadius: `${radius.pill}px`,
  border: '1px solid',
  borderColor: 'primary.light',
  color: 'primary.main',
  whiteSpace: 'nowrap',
};

const CHIP_SX = {
  px: 2,
  py: 0.5,
  minWidth: 60,
  borderRadius: 1,
  fontSize: 14,
  fontWeight: 600,
  textAlign: 'center',
};

export interface CreativeAttributesAnalysisProps {
  result: unknown;
  creativeId: string;
  networkId: number;
  platformSlug: string;
}

function FeatureScore({ feature }: { feature: AnalysedFeature }): ReactNode {
  const halves = [
    { label: 'Performance', value: feature.performance },
    { label: 'Best practice', value: feature.bestPractice },
  ].filter((half) => half.value !== null);

  return (
    <Tooltip
      placement="top-start"
      title={
        <Stack sx={{ gap: 0.5 }}>
          {feature.fromBrandGuidelines ? (
            <Typography sx={{ fontWeight: 600 }}>Brand Guideline</Typography>
          ) : null}
          {halves.map((half) => (
            <Stack key={half.label} direction="row" sx={{ gap: 2, whiteSpace: 'nowrap' }}>
              <Typography sx={{ flex: 1 }}>{half.label}</Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {`${half.value?.toFixed(1)} / ${FEATURE_SCORE_MAX}`}
              </Typography>
            </Stack>
          ))}
        </Stack>
      }
    >
      <Box sx={CHIP_SX}>
        <ScoreText score={feature.score} />
      </Box>
    </Tooltip>
  );
}

function ScoreText({ score }: { score: number | null }): ReactNode {
  return (
    <Typography
      component="span"
      sx={(theme) => ({
        fontSize: 14,
        fontWeight: 600,
        color: toneOf(theme, score === null ? null : (score / FEATURE_SCORE_MAX) * SCORE_SCALE),
      })}
    >
      {score === null ? '—' : score.toFixed(1)}
    </Typography>
  );
}

function FeatureLine({ feature }: { feature: AnalysedFeature }): ReactNode {
  return (
    <Stack direction="row" sx={ROW_SX}>
      <Stack sx={{ minWidth: 0 }}>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <FormFieldLabel sx={{ fontSize: 9, color: 'text.muted' }}>
            {feature.feature.toUpperCase()}
          </FormFieldLabel>
          {feature.recommendation === null ? null : (
            <Tooltip placement="top-start" title={feature.recommendation}>
              <LightbulbOutlined sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
        </Stack>
        <Typography sx={{ fontSize: 13 }}>{feature.tag}</Typography>
      </Stack>
      <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
        {feature.fromBrandGuidelines ? (
          <Stack direction="row" sx={SOURCE_SX}>
            <ShieldOutlined sx={{ fontSize: 14 }} />
            <Typography sx={{ fontWeight: 600 }}>Brand Guidelines</Typography>
          </Stack>
        ) : null}
        <FeatureScore feature={feature} />
      </Stack>
    </Stack>
  );
}

function AttributeAccordion({
  attribute,
  startOpen,
}: {
  attribute: AnalysedAttribute;
  startOpen: boolean;
}): ReactNode {
  const [open, setOpen] = useState(startOpen);

  return (
    <Stack sx={CARD_SX}>
      <Stack
        direction="row"
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        sx={{ ...HEADER_SX, border: 0, bgcolor: 'transparent', width: '100%' }}
      >
        <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{attribute.name}</Typography>
          <Typography sx={{ color: 'text.muted' }}>
            {`Weight: ${attribute.weightPercent}%`}
          </Typography>
          <Tooltip
            placement="top-start"
            title={
              <Stack sx={{ gap: 1 }}>
                <Typography>
                  {`Category score: ${attribute.categoryScore.toFixed(2)} / ${attribute.categoryMax.toFixed(2)}`}
                </Typography>
                <Typography>
                  {`Based on your tags, this creative earned ${attribute.percentOfMax}% of the potential points available in this category.`}
                </Typography>
              </Stack>
            }
          >
            <InfoOutlined sx={{ fontSize: 16, color: 'text.muted' }} />
          </Tooltip>
        </Stack>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <ScorePip score={attribute.score === null ? null : attribute.score / SCORE_SCALE} />
          <IconButton component="span" size="small" sx={{ color: 'text.muted' }}>
            {open ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Stack>
      </Stack>

      <Collapse in={open}>
        <Stack sx={{ gap: 2.5, px: 4, pb: 2.5 }}>
          {attribute.groups.map((group) => (
            <Stack key={group.abcd} sx={{ gap: 1 }}>
              {group.abcd === '' ? null : (
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.muted' }}>
                  {group.abcd}
                </Typography>
              )}
              {group.features.map((feature) => (
                <FeatureLine key={`${feature.feature}-${feature.tag}`} feature={feature} />
              ))}
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}

export function CreativeAttributesAnalysis({
  result,
  creativeId,
  networkId,
  platformSlug,
}: CreativeAttributesAnalysisProps): ReactNode {
  const attributes = analysedAttributes(result, creativeId, networkId);
  if (attributes.length === 0) return null;

  return (
    <Stack data-testid="creative-attributes-analysis" sx={{ gap: 2 }}>
      <Box>
        <Typography variant="h3" sx={{ fontSize: 15 }}>
          Creative Attributes Analysis
        </Typography>
        <Typography sx={{ color: 'text.muted' }}>
          {FRAMEWORK_TEXT[platformSlug] ?? FRAMEWORK_TEXT['google']}
        </Typography>
      </Box>
      <Stack sx={{ gap: 2 }}>
        {attributes.map((attribute, index) => (
          <AttributeAccordion key={attribute.name} attribute={attribute} startOpen={index === 0} />
        ))}
      </Stack>
    </Stack>
  );
}
